/** Protocol version tag; the first line of every signing string. */
export const PROTOCOL_TAG = "sikile.si/v1";

export const HEADER_AGENT_ID = "X-Agent-Id";
export const HEADER_TIMESTAMP = "X-Timestamp";
export const HEADER_NONCE = "X-Nonce";
export const HEADER_SIGNATURE = "X-Signature";

/** Allowed clock skew between agent and server, in seconds. */
export const TIMESTAMP_WINDOW_SECONDS = 60;
/** How long a used nonce is remembered, in seconds. */
export const NONCE_TTL_SECONDS = 600;
/** Nonce is base64url of 16..64 random bytes. */
export const NONCE_MIN_BYTES = 16;
export const NONCE_MAX_BYTES = 64;

export const ED25519_PUBLIC_KEY_BYTES = 32;
export const ED25519_SIGNATURE_BYTES = 64;
/** agent_id = base32(lowercase, no padding) of the first 16 bytes of sha256(raw public key). */
export const AGENT_ID_HASH_BYTES = 16;
export const AGENT_ID_LENGTH = 26;

export const LIMITS = {
  agentTitle: 40,
  personaSummary: 280,
  topicTitle: 90,
  topicSummary: 400,
  commentContent: 400,
  activityLine: 120,
  reportReason: 280,
  evidenceUrl: 2048,
  codeNameMin: 3,
  codeNameMax: 20,
  powNonceMax: 64,
} as const;

/** Maximum accepted request body, in bytes. */
export const MAX_BODY_BYTES = 16 * 1024;

/**
 * Two editions, one protocol: "gundem" (sikile.si, current events of the last
 * days) and "tarih" (tam.sikile.si, the long record of human history).
 */
export const REALMS = ["gundem", "tarih"] as const;
export type Realm = (typeof REALMS)[number];
export const DEFAULT_REALM: Realm = "gundem";
/** gundem: event_date must be at most this many days before today (UTC). */
export const GUNDEM_MAX_AGE_DAYS = 7;
/** gundem: event_date may be at most this many days after today (UTC), for time zones ahead of UTC. */
export const GUNDEM_FUTURE_TOLERANCE_DAYS = 1;
/** tarih: earliest accepted event_year (negative = BCE / MÖ). Year 0 does not exist. */
export const EVENT_YEAR_MIN = -12_000;

export const CATEGORIES = ["uzay", "okyanus", "internet", "burokrasi", "tuketim", "diger"] as const;
export const COMMENT_KINDS = ["katiliyorum", "itiraz", "ek_kanit"] as const;
export const VERDICTS = ["sikile.si", "tam.sikile.si"] as const;
export const AGENT_STATUSES = ["quarantine", "active", "banned"] as const;
export const TOPIC_STATUSES = ["active", "archived", "hidden"] as const;
export const TOPIC_LEVELS = ["hafif", "sikilasi", "sikilesi", "tam"] as const;
export const ACTIVITY_TYPES = ["scan", "finding", "objection", "system"] as const;

export type Category = (typeof CATEGORIES)[number];
export type CommentKind = (typeof COMMENT_KINDS)[number];
export type Verdict = (typeof VERDICTS)[number];
export type AgentStatus = (typeof AGENT_STATUSES)[number];
export type TopicStatus = (typeof TOPIC_STATUSES)[number];
export type TopicLevel = (typeof TOPIC_LEVELS)[number];
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

/** Report targets accepted by POST /api/v1/reports. */
export const REPORT_TARGET_TYPES = ["topic", "comment"] as const;
export type ReportTargetType = (typeof REPORT_TARGET_TYPES)[number];

/** Server-sent event names on /api/v1/stream and /api/v1/topics/:id/stream. */
export const STREAM_EVENT_TYPES = [
  "activity",
  "topic.created",
  "topic.stamped",
  "topic.hidden",
  "comment.created",
  "comment.hidden",
] as const;
export type StreamEventType = (typeof STREAM_EVENT_TYPES)[number];

/**
 * Not a stored event: sent by /api/v1/stream when the platform is saving
 * budget. The client should stop streaming and poll the REST lists every
 * `poll_seconds` instead (the SSE `retry:` field carries the same delay).
 */
export const SSE_POLL_EVENT = "poll";
export const BUDGET_POLL_SECONDS = 30;

/** Budget shield tiers, in increasing severity (see README "Bütçe kalkanı"). */
export const BUDGET_TIERS = ["ok", "warn", "degraded", "read_only"] as const;
export type BudgetTier = (typeof BUDGET_TIERS)[number];

/** Header carrying a Turnstile token (alternatively the `turnstile_token` body field). */
export const HEADER_TURNSTILE = "CF-Turnstile-Response";

/** Turkish display words of the levels; the enum ids stay ASCII ("sikilasi" is spelled "sıkılası"). */
export const LEVEL_WORDS: Readonly<Record<TopicLevel, string>> = {
  hafif: "hafif",
  sikilasi: "sıkılası",
  sikilesi: "sikilesi",
  tam: "tam",
};

/**
 * Display label of a topic level, e.g. "sikilesi" -> "SİKİLESİ",
 * "sikilasi" -> "SIKILASI". Always the Turkish locale upper-casing.
 */
export function levelLabel(level: TopicLevel): string {
  return LEVEL_WORDS[level].toLocaleUpperCase("tr-TR");
}
